import InfoIcon from '@heroicons/react/20/solid/InformationCircleIcon';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import TimeGoneIcon from 'jsx:../../../assets/time-gone-icon.svg';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as z from 'zod';
import { AvailabilityDots } from '../../../../shared/components/availability-dots';
import { DateTime } from '../../../../shared/components/date-time';
import { TIMEZONE } from '../../../../shared/constants';
import { useToday } from '../../../../shared/hooks/use-today';
import { useReserveTime } from '../../api/use-reserve-time';
import { Calendar } from '../../components/calendar';
import { Dialog, DialogContent } from '../../components/dialog';
import { ProgressLayout } from '../../components/layouts/progress-layout';
import { SpecialistDrawer } from '../../components/specialist-card';
import { Button } from '../../components/ui/button';
import { RadioGroup } from '../../components/ui/radio-group-list';
import { FORMAT_LT } from '../../constants/misc';
import { useViewTransitionNavigate } from '../../hooks/useNavigateWithTransition';
import { useInviteStore } from '../../store/use-invite-store';
import { API_DATE_FORMAT } from '../../utils/constants';

dayjs.extend(timezone);

const schema = z.object({
  selectedDate: z.string(),
  appointmentSlotId: z.string().min(1),
});

function updateAvailability(availability, slotTime) {
  if (parseInt(slotTime.split(':')[0], 10) < 12) {
    return ['green', availability[1]];
  } else {
    return [availability[0], 'green'];
  }
}

function useAvailableSpecialistsByDay() {
  const { invite } = useInviteStore();
  return useMemo(() => {
    const chosenPractitionerId = invite.onlineBookingData.specialistId;

    // Create a map from days to specialists have have available slots on that
    // day and an availability dots pair for that day.
    const intermediate = invite.practitioners
      .filter(
        (practitioner) =>
          chosenPractitionerId === 'any' || practitioner.practitionerId === chosenPractitionerId,
      )
      .filter((practitioner) => practitioner.calendarSlots.length > 0)
      .reduce((acc, practitioner) => {
        for (const slot of practitioner.calendarSlots) {
          if (!Object.hasOwn(acc, slot.date)) {
            acc[slot.date] = { availability: ['gray', 'gray'] };
          }
          if (!Object.hasOwn(acc[slot.date], practitioner.practitionerId)) {
            // Assign practitioner and slots to this day
            const daySlots = practitioner.calendarSlots.filter((s) => s.date === slot.date);
            acc[slot.date][practitioner.practitionerId] = {
              ...practitioner,
              calendarSlots: daySlots,
            };

            // Update day availability
            for (const s of daySlots) {
              acc[slot.date].availability = updateAvailability(
                acc[slot.date].availability,
                s.slotTime,
              );
            }
          }
        }
        return acc;
      }, {});

    // Reconstruct a slot from the current appointment and add it to the map
    // above because after confirming the appointment the backend won't send
    // back this slot as available anymore, but we still want to show it to the
    // user.
    if (invite.appointment) {
      const practitionerId = invite.appointment.practitioner.practitionerId;
      const calendarSlot = {
        slotId: invite.appointment.calendarListSlotId ?? invite.appointment.calendarSlotId,
        date: invite.appointment.appointmentDate,
        slotTime: invite.appointment.appointmentTime,
      };

      // Patch day
      if (!Object.hasOwn(intermediate, calendarSlot.date)) {
        intermediate[calendarSlot.date] = {
          availability: updateAvailability(['gray', 'gray'], calendarSlot.slotTime),
        };
      } else {
        intermediate[calendarSlot.date].availability = updateAvailability(
          intermediate[calendarSlot.date].availability,
          calendarSlot.slotTime,
        );
      }

      // Patch practitioner
      if (!Object.hasOwn(intermediate[calendarSlot.date], practitionerId)) {
        intermediate[calendarSlot.date][practitionerId] = {
          practitionerId,
          practitionerImageUrl: null,
          calendarSlots: [calendarSlot],
        };
      } else if (
        !intermediate[calendarSlot.date][practitionerId].calendarSlots.some(
          (x) => x.slotId === calendarSlot.slotId,
        )
      ) {
        intermediate[calendarSlot.date][practitionerId].calendarSlots.push(calendarSlot);
        intermediate[calendarSlot.date][practitionerId].calendarSlots.sort((a, b) =>
          a.slotTime.localeCompare(b.slotTime),
        );
      }
    }

    // Convert the map of specialists to an array.
    return Object.fromEntries(
      Object.entries(intermediate).map(([k, { availability, ...rest }]) => [
        k,
        { availability, specialists: Object.values(rest) },
      ]),
    );
  }, [invite.appointment, invite.onlineBookingData.specialistId, invite.practitioners]);
}

const AvailabilityContext = createContext();

function useAvailability(date) {
  const state = useContext(AvailabilityContext);
  return state[date] ?? {};
}

export function Appointment(props) {
  const { invite } = useInviteStore();
  const { t } = useTranslation(['page.appointment']);
  const { next } = props;
  const reserveTime = useReserveTime();
  const navigate = useViewTransitionNavigate();
  const [openTimeModal, setOpenTimeModal] = useState(false);
  const today = useToday().format(API_DATE_FORMAT);

  const availableSpecialists = useAvailableSpecialistsByDay();
  const [initialDate] = useState(() => {
    // Initial state is current appointment date, the first available date or
    // otherwise today.
    return (
      invite.appointment?.appointmentDate ??
      Object.keys(availableSpecialists ?? { today })
        .toSorted()
        .at(0)
    );
  });

  const form = useForm({
    defaultValues: {
      selectedDate: invite.appointment?.appointmentDate ?? initialDate,
      appointmentSlotId:
        invite.appointment?.calendarListSlotId ?? invite.appointment?.calendarSlotId ?? '',
    },
    resolver: zodResolver(schema),
    mode: 'onChange',
  });
  const { reset: resetForm } = form;

  const onSubmit = useCallback(
    async ({ appointmentSlotId }) => {
      try {
        await reserveTime.execute(appointmentSlotId);
        navigate(`/${invite.reference}/${next.path}`);
      } catch {
        resetForm();
        setOpenTimeModal(true);
      }
    },
    [invite.reference, navigate, next.path, reserveTime, resetForm],
  );

  return (
    <FormProvider {...form}>
      <form id="patch-invite-form" className="contents" onSubmit={form.handleSubmit(onSubmit)}>
        <ProgressLayout {...props}>
          <h1 className="title mb-5">{t('title')}</h1>
          <AvailabilityContext.Provider value={availableSpecialists}>
            <Calendar initialDate={initialDate} DayComponent={CalendarDayAvailability} />
            <AppointmentFields />
          </AvailabilityContext.Provider>
          <ChooseTimeModal openModal={openTimeModal} setOpenModal={setOpenTimeModal} />
        </ProgressLayout>
      </form>
    </FormProvider>
  );
}

function AppointmentFields() {
  const { t } = useTranslation('page.appointment');
  const selectedDate = useWatch({ name: 'selectedDate' });
  const { specialists } = useAvailability(selectedDate);

  return specialists ? (
    <div>
      {specialists.map((specialist) => {
        const options = specialist.calendarSlots.map((slot) => ({
          value: slot.slotId,
          label: dayjs.tz(`2000-01-01T${slot.slotTime}`, TIMEZONE).format(FORMAT_LT),
        }));

        return (
          <RadioGroup
            layout="grid"
            key={specialist.practitionerId}
            name="appointmentSlotId"
            label={<InfoButton specialist={specialist} />}
            options={options}
          />
        );
      })}
    </div>
  ) : (
    <p className="text-light-500">{t('noTimeText')}</p>
  );
}

function InfoButton({ specialist }) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation('page.specialist', {
    keyPrefix: `specialists.${specialist.practitionerId}`,
  });
  return (
    <>
      <button type="button" className="flex items-center gap-1" onClick={() => setOpen(true)}>
        <span>{t('name')}</span>
        <InfoIcon className="size-5 text-primary" aria-hidden="true" />
      </button>
      <SpecialistDrawer
        menuOpen={open}
        setMenuOpen={setOpen}
        url={specialist.practitionerImageUrl}
        specialistId={specialist.practitionerId}
      />
    </>
  );
}

function ChooseTimeModal({ openModal, setOpenModal }) {
  const { t } = useTranslation();
  const { t: tAppointment } = useTranslation('page.appointment');

  return (
    <Dialog open={openModal} onOpenChange={setOpenModal} className="p-4 bg-modalBg/75">
      <DialogContent
        className="Dialog p-4 pt-5 flex flex-col items-center container w-full overflow-y-auto rounded-lg bg-white text-body-dark shadow-xl text-center max-w-[480px]"
        direction="bottom"
      >
        <div
          className="mb-3 size-15 flex items-center justify-center bg-primary-100 rounded-full"
          aria-hidden="true"
        >
          <TimeGoneIcon className="text-light-900" />
        </div>
        <h3 className="mb-2 title-sm font-medium">{tAppointment('modal.title')}</h3>
        <p className="caption mb-5 text-light-500">{tAppointment(t('modal.text'))}</p>
        <Button onClick={() => setOpenModal(false)}>{tAppointment(t('modal.buttonLabel'))}</Button>
      </DialogContent>
    </Dialog>
  );
}

export function CalendarDayAvailability({ day }) {
  const today = useToday();
  const dayValue = day.format(API_DATE_FORMAT);
  const { availability } = useAvailability(dayValue);
  const { setValue, watch } = useFormContext();
  const selectedValue = watch('selectedDate');

  let variant;
  if (selectedValue === dayValue) {
    variant = 'selected';
  } else if (day.isSame(today, 'day')) {
    variant = 'today';
  } else {
    variant = availability ? 'available' : 'unavailable';
  }

  const handleSelectDay = useCallback(() => {
    setValue('selectedDate', dayValue);
    setValue('appointmentSlotId', '');
  }, [dayValue, setValue]);

  return (
    <div className="flex-1 text-center">
      <span
        className="text-small w-full flex items-center justify-center h-8 mb-1 text-body-dark data-[weekend=true]:text-gray-300"
        data-weekend={day.isoWeekday() >= 6}
      >
        {day.format('ddd').charAt(0)}
      </span>
      <button
        onClick={handleSelectDay}
        className="p-0.5 flex-col mx-auto flex w-full h-9 items-center justify-start rounded-lg text-center relative hover:text-current hover:bg-gray-200 disabled:hover:bg-inherit data-[variant=unavailable]:text-gray-300 data-[variant=today]:font-semibold data-[variant=today]:text-blue-600 data-[variant=selected]:bg-blue-600 data-[variant=selected]:text-white data-[variant=selected]:hover:bg-blue-600"
        type="button"
        disabled={!availability}
        data-testid={dayValue}
        data-variant={variant}
      >
        <DateTime t={day} format="D" />
        <div className="absolute bottom-0 pb-1.25">
          <AvailabilityDots availability={availability} />
        </div>
      </button>
    </div>
  );
}
